import { html, unsafeCSS } from 'lit';
import { property, state } from 'lit/decorators.js';
import DERModuleElement from '~/modules/utils/der-module-element';

import { Task } from '@lit/task';
import { ItineraryItem } from './types';

import { styles as globalStyles } from '~/ui/scss/main.styles';
import { styles } from './der-content-itinerary.styles';

import '~/ui/components/accordion';
import '~/ui/components/module-title';
import '~/ui/components/stepper';
import '~/ui/objects/image-grid';

import '~/ui/objects/module/body';
import '~/ui/objects/module/module';
import '~/ui/objects/module/title';

export class Itinerary extends DERModuleElement {
    static override styles = [unsafeCSS(globalStyles), unsafeCSS(styles)];

    @property({ type: String }) identifier = '';

    @property({ type: String, attribute: 'header-suptitle' })
    suptitle = '';

    @property({ type: String, attribute: 'header-title' })
    override title = '';

    @property({ type: String, attribute: 'header-intro' })
    intro = '';

    @state()
    itineraryList: ItineraryItem[] = [];

    private getErrorMessage(lang: string): string {
        if (lang === 'fr-CH') {
            return "L'itinéraire n'a pas pu être chargé.";
        }
        return 'Der Reiseverlauf konnte nicht geladen werden.';
    }

    private buildSteps() {
        return this.itineraryList.map((item, index) => ({
            label: item.label,
            content: html`
                <der-ui-accordion title="${item.title}" .open="${index === 0}">
                    <p>${item.description}</p>
                    <der-ui-image-grid .images=${item.images}></der-ui-image-grid>
                </der-ui-accordion>
            `,
        }));
    }

    private _contentTask = new Task(this, {
        task: async ([identifier, lang]: [string, string]) => {
            const shortLang = lang.slice(0, 2);
            const url = `https://dtchcontenthubprod.blob.core.windows.net/public/roundtrips/itineraries/${identifier}-${shortLang}.json`;

            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`Failed to fetch itinerary: ${response.statusText}`);
            }

            this.itineraryList = await response.json();
        },
        args: () => [this.identifier, this.lang] as [string, string],
    });

    override render() {
        const steps = this.buildSteps();

        return this._contentTask.render({
            pending: () => html``,
            /**
             * No loading state as the module is expected to be used
             */
            complete: () => html`
                <div class="m-itinerary">
                    <der-ui-title-wrapper>
                        <der-ui-module-title
                            suptitle="${this.suptitle}"
                            title="${this.title}"
                            intro="${this.intro}"
                            module-name="m-usp"
                        ></der-ui-module-title>
                    </der-ui-title-wrapper>

                    <der-ui-body-wrapper>
                        <der-ui-stepper .steps=${steps}></der-ui-stepper>
                    </der-ui-body-wrapper>
                </div>
            `,
            /**
             * No specific error handling, just a simple message
             */
            error: () => html`
                <div class="m-itinerary">
                    <der-ui-title-wrapper>
                        <der-ui-module-title
                            suptitle="${this.suptitle}"
                            title="${this.title}"
                            intro="${this.intro}"
                            module-name="m-usp"
                        ></der-ui-module-title>
                    </der-ui-title-wrapper>
                    <p class="m-itinerary__error-msg">${this.getErrorMessage(this.lang)}</p>
                </div>
            `,
        });
    }
}
